import { unifiedClient } from '@/lib/unified-fetch';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * Event Revenue service functions using unified admin client
 * Handles event revenue items (source, itemId, itemName) for events
 */

// Types based on API schema
export type EventRevenueItem = {
  id?: number; // Optional for creation
  eventId: number | null;
  source: string;
  itemId: string;
  itemName: string;
};

export type EventRevenueCreateUpdate = {
  id?: number;
  eventId: number | null;
  source: string;
  itemId: string;
  itemName: string;
};

/**
 * Hook to fetch event revenue items by eventId
 */
export const useEventRevenueItems = (eventId: number | null) => {
  return useQuery({
    queryKey: ['eventRevenue', eventId],
    queryFn: async (): Promise<EventRevenueItem[]> => {
      if (!eventId) return [];
      
      const response = await unifiedClient.admin.GET('/event_EventRevenue/', {
        params: {
          query: {
            filter: {
              eventId: eventId
            },
            limit: 100, // Should be enough for event items
            offset: 0,
          }
        },
      });

      console.log('useEventRevenueItems - API response:', response);
      console.log('useEventRevenueItems - response.data:', response.data);

      if (!response.data) {
        throw new Error('Failed to fetch event revenue items: No data returned');
      }

      // The API returns data directly as an array
      const items = response.data || [];
      console.log('useEventRevenueItems - returning items:', items);
      
      return items;
    },
    enabled: !!eventId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to save multiple event revenue items
 */
export const useSaveEventRevenueItems = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { eventId: number; items: EventRevenueCreateUpdate[] }): Promise<void> => {
      const { eventId, items } = data;

      // Transform items to include eventId
      const itemsWithEventId = items.map(item => ({
        ...item,
        eventId,
      }));

      const response = await unifiedClient.admin.PUT('/event_EventRevenue/many', {
        body: itemsWithEventId as any, // Type assertion to bypass OpenAPI schema limitations
      });

      if (!response.response?.ok) {
        throw new Error('Failed to save event revenue items');
      }

      // Response only returns status 200, no data
    },
    onSuccess: (_, variables) => {
      // Invalidate the event revenue items query for this event
      queryClient.invalidateQueries({
        queryKey: ['eventRevenue', variables.eventId],
      });
      
      // Also invalidate general event revenue queries
      queryClient.invalidateQueries({
        queryKey: ['eventRevenue'],
      });
    },
  });
};

/**
 * Hook to delete event revenue items by IDs
 */
export const useDeleteEventRevenueItems = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { eventId: number; ids: number[] }): Promise<void> => {
      const { eventId, ids } = data;

      console.log('useDeleteEventRevenueItems - Deleting items with IDs:', ids);

      // Call DELETE API for each ID
      const deletePromises = ids.map(async (id) => {
        const response = await unifiedClient.admin.DELETE('/event_EventRevenue/{id}', {
          params: {
            path: { id },
          },
        });

        console.log(`useDeleteEventRevenueItems - Delete response for ID ${id}:`, response);

        if (!response.response?.ok) {
          throw new Error(`Failed to delete event revenue item with ID ${id}`);
        }
      });

      // Wait for all deletions to complete
      await Promise.all(deletePromises);
    },
    onSuccess: (_, variables) => {
      // Invalidate the event revenue items query for this event
      queryClient.invalidateQueries({
        queryKey: ['eventRevenue', variables.eventId],
      });
      
      // Also invalidate general event revenue queries
      queryClient.invalidateQueries({
        queryKey: ['eventRevenue'],
      });
    },
  });
};

/**
 * Hook with loading state management for easier UI integration
 */
export const useSaveEventRevenueItemsWithState = () => {
  const mutation = useSaveEventRevenueItems();

  const saveItems = (
    eventId: number,
    items: EventRevenueCreateUpdate[],
    options?: {
      onSuccess?: () => void;
      onError?: (error: any) => void;
    }
  ) => {
    return mutation.mutate(
      { eventId, items },
      {
        onSuccess: () => {
          options?.onSuccess?.();
        },
        onError: (error) => {
          options?.onError?.(error);
        },
      }
    );
  };

  return {
    saveItems,
    isLoading: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
};

/**
 * Hook with loading state management for delete operations
 */
export const useDeleteEventRevenueItemsWithState = () => {
  const mutation = useDeleteEventRevenueItems();

  const deleteItems = (
    eventId: number,
    ids: number[],
    options?: {
      onSuccess?: () => void;
      onError?: (error: any) => void;
    }
  ) => {
    return mutation.mutate(
      { eventId, ids },
      {
        onSuccess: () => {
          options?.onSuccess?.();
        },
        onError: (error) => {
          options?.onError?.(error);
        },
      }
    );
  };

  return {
    deleteItems,
    isLoading: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
};
